// @flow

import _ from 'lodash';
import ko from 'knockout';
import window from 'window';
import type { Observable, ComponentInfo } from "rgs-knockout/types";
import FioInput from '../fio-input/fio-input';
import { withTypeahead } from '../dropdown/components/typeahead/typeahead';
import type { FioInputArguments } from '../fio-input/fio-input';

function useDefault (mapper) {
	return (item: any, inputValue: string) => {
		const mappedValue = (mapper(item) || '').toUpperCase();

		return mappedValue.includes(inputValue.toUpperCase());
	};
}

export type TypeaheadOptions = {
	fetch: (search: string, params: any) => Promise<*>;
	onInputChange: (search: string) => void;
	onItemClick: (item: any) => void;
	onMapItemValue: (suggest: any) => void;
}

export type TypeaheadFioInputOptions = {
	fioOptions: FioInputArguments,
	typeaheadOptions: TypeaheadOptions,
};

export default class TypeaheadFioInput extends withTypeahead(FioInput) {
	isOpened: Observable<boolean> = ko.observable(false);
	total: Observable<number> = ko.observable(0);
	filterItems: Observable<[]> = ko.observableArray([]);
	isFetching: Observable<boolean> = ko.observable(false);
	typeaheadOptions: TypeaheadOptions;

	constructor ({ fioOptions, typeaheadOptions }: TypeaheadFioInputOptions, ref: ComponentInfo) {
		super(fioOptions, ref);

		this.ref = ref.element;

		this.typeaheadOptions = typeaheadOptions;

		this.parts.btnMore.visible = ko.observable(false);
		this.parts.btnMore.onClick = _.noop;
		this.parts.label.onClick = _.noop;

		window.addEventListener('click', this._onHideIfOuterClick, true);
	}

	handleClickOnLayout = () => {
		this.isOpened(!this.isOpened.peek());
	};

	handleSwitchToFullMode = () => this._mode('full');

	assignHandlers () {
		return {
			onItemClick: this._onItemClick.bind(this),
			onInputChange: (value: string) => this._onInputChange(value),
			onMapItemValue: this._onMapItemValue.bind(this),
			onFilterBy: useDefault(this._onMapItemValue.bind(this)),
		};
	}

	createPrimary () {
		this._fullName.subscribe(this._onInputChange.bind(this));

		return {
			isFetching: ko.pureComputed(() => false),
			inputValue: ko.pureComputed({
				read: () => this._fullName(),
				write: update => {
					const source = _.isObject(update)
						? this._onMapItemValue(update) : update;

					this._fullName(source);
				},
			}),
			tempInputValue: ko.observable(),
			items: ko.pureComputed(() => this.filterItems()),
		};
	}

	async _onInputChange (search: string) {
		if (this.typeaheadOptions.onInputChange) {
			this.typeaheadOptions.onInputChange(search);
		}

		if (this.typeaheadOptions.fetch) {
			try {
				this.isFetching(true);
				const data = await this.typeaheadOptions.fetch(search, { take: 10 });
				this.filterItems(data || []);
				this.total(this.filterItems().length);
			} finally {
				this.isFetching(false);
			}
		}
	}

	async _onItemClick (item: any) {
		if (this.typeaheadOptions.onItemClick) {
			await this.typeaheadOptions.onItemClick(item);
		}
		this.isOpened(false);
	}

	async _onMapItemValue (suggest: any) {
		if (this.typeaheadOptions.onMapItemValue) {
			return this.typeaheadOptions.onMapItemValue(suggest);
		}

		return suggest;
	}

	_onHideIfOuterClick = ({ target }: { target: Node }): void => {
		if (document.body && !document.body.contains(this.ref)) { // eslint-disable-line no-undef
			ko.cleanNode(this.ref);
			return;
		}
		if (this.isOpened.peek() && !this.ref.contains(target)) {
			this.isOpened(false);
		}
	};

	dispose (): void {
		window.removeEventListener('click', this._onHideIfOuterClick);
	}
}